import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CustomerEnrollment from "./CustomerEnrollment";
import ConfirmationDialog from "./ConfirmationDialog";
import {
  isContactWorkflow,
  isUserWorkflow,
  supportsSelection,
} from "./workflowParticipants";

export default function ParticipantEnrollment(props) {
  if (
    !(
      (isUserWorkflow(props.workflow) &&
        supportsSelection(props.workflow, "user")) ||
      (isContactWorkflow(props.workflow) &&
        supportsSelection(props.workflow, "contact"))
    )
  )
    return (
      <Alert severity="info">
        Enrollment options are unavailable. Please reload the page.
      </Alert>
    );
  if (isUserWorkflow(props.workflow))
    return (
      <CustomerEnrollment
        key={`${props.workflow.id}:${props.versionId}`}
        {...props}
      />
    );
  return <ContactEnrollment {...props} />;
}

function ContactEnrollment({
  api,
  workflow,
  versionId,
  onEnrolled,
  disabled = false,
  disabledReason = "",
}) {
  const contactWorkflow = isContactWorkflow(workflow);
  const searchMethod = contactWorkflow
    ? "searchEligibleContacts"
    : "searchEligibleUsers";
  const participantLabel = contactWorkflow ? "contact" : "user";
  const [search, setSearch] = useState("");
  const [participants, setParticipants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    endCursor: null,
    hasNextPage: false,
  });
  useEffect(() => {
    setSelected(null);
    setSearch("");
    setParticipants([]);
    setConfirmOpen(false);
  }, [workflow.id, versionId]);
  useEffect(() => {
    if (disabled) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result = await api[searchMethod]({
          workflowId: workflow.id,
          search,
          first: 25,
        });
        if (active) {
          setParticipants((result.edges || []).map((edge) => edge.node));
          setPageInfo(result.pageInfo || {});
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, workflow.id, search, disabled, searchMethod]);
  const loadMore = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api[searchMethod]({
        workflowId: workflow.id,
        search,
        first: 25,
        after: pageInfo.endCursor,
      });
      setParticipants((items) => [
        ...items,
        ...(result.edges || []).map((edge) => edge.node),
      ]);
      setPageInfo(result.pageInfo || {});
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };
  const enroll = async () => {
    if (!selected || disabled || loading) return;
    setConfirmOpen(false);
    setLoading(true);
    setError("");
    try {
      const result = contactWorkflow
        ? await api.enrollContacts([selected.id], workflow.id, versionId)
        : await api.enrollUser(selected.id, workflow.id, versionId);
      enqueueSnackbar(
        contactWorkflow
          ? result.results.map((item) => item.message).join(" ")
          : result.message,
        {
          variant:
            result.success || (contactWorkflow && result.alreadyEnrolled > 0)
              ? "success"
              : "error",
        },
      );
      if (result.success || (contactWorkflow && result.alreadyEnrolled > 0)) {
        setParticipants((items) => items.filter((u) => u.id !== selected.id));
        setSelected(null);
        onEnrolled?.();
      }
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6">
        {contactWorkflow ? "Enroll outreach contact" : "Enroll existing user"}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Search {participantLabel}s eligible for {workflow.workflowTypeLabel} and
        enroll one explicitly.
      </Typography>
      {disabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {disabledReason}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Autocomplete
          fullWidth
          options={participants}
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selected}
          loading={loading}
          disabled={disabled}
          getOptionLabel={(u) =>
            `${u.firstName || ""} ${u.lastName || ""} (${u.email})`
          }
          onChange={(_, value) => setSelected(value)}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") setSearch(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Eligible ${participantLabel}`}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          disabled={disabled || !selected || loading}
          sx={{ alignSelf: "flex-start" }}
          onClick={() => setConfirmOpen(true)}
        >
          Enroll
        </Button>
      </Stack>
      {pageInfo.hasNextPage && (
        <Button
          sx={{ mt: 1 }}
          onClick={loadMore}
          disabled={disabled || loading}
        >
          Load more eligible {participantLabel}s
        </Button>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        title={`Enroll ${participantLabel}?`}
        message={`Enroll ${selected?.email || `this ${participantLabel}`} in ${workflow.workflowTypeLabel}?`}
        confirmLabel="Enroll"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={enroll}
      />
    </Paper>
  );
}
